import { assetsUrl } from "@api";

// Eén plek voor alle afbeeldings-URL's van producten, recepten en categorieën.
//
// De API stuurt per item een `image_version` mee (12 hex-tekens, uniek per
// schrijfactie in de bucket, ook na terugzetten). Die hangen we als ?v=<versie>
// aan de URL: een nieuwe foto heeft een nieuwe versie en dus een nieuwe URL.
// Caddy laat URL's met zo'n versie daarom een jaar cachen (immutable) en de app
// (expo-image) ziet een nieuwe foto toch meteen, zodra de productlijst opnieuw
// geladen is. Elke andere ?v= (zoals de oude ?v=3) krijgt maar een uur.
//
// Zonder versie (oude backend, of een item zonder foto) blijft de URL precies
// zoals hij was.

export type ImageKind = "product" | "recipe" | "category";

const prefixes: Record<ImageKind, string> = {
  product: "/images/products/",
  recipe: "/images/recepten/",
  category: "/images/categories/",
};

// categorySlug spiegelt de server (imagever.CategorySlug): kleine letters, zonder
// spaties, haakjes en &.
export function categorySlug(name: string | undefined | null): string {
  return (name || "").toLowerCase().replaceAll(" ", "").replaceAll("(", "").replaceAll(")", "").replaceAll("&", "");
}

// imageUrl bouwt de URL. key is de pid (product), de bestandsnaam uit het recept
// (recipe) of de slug (category).
export function imageUrl(kind: ImageKind, key: string | number, version?: string | null): string {
  const file = kind === "recipe" ? `${key}` : `${key}.jpg`;
  const query = version ? `?v=${encodeURIComponent(version)}` : "";
  return assetsUrl + prefixes[kind] + file + query;
}

// Versies per pid uit /products/v2/all, voor de plekken die alleen een pid
// kennen (lijstjes, bestelgeschiedenis, receptingrediënten). Gevuld door
// useAppData zodra de productlijst binnen is.
let productVersions = new Map<number, string>();

export function setProductImageVersions(products: { pid: number; image_version?: string }[] | undefined) {
  const next = new Map<number, string>();
  products?.forEach((p) => {
    if (p?.image_version) {
      next.set(p.pid, p.image_version);
    }
  });
  productVersions = next;
}

// productImageUrl: met de versie van het product zelf, anders die uit de
// productlijst.
export function productImageUrl(pid: number | string, version?: string | null): string {
  return imageUrl("product", pid, version || productVersions.get(Number(pid)));
}

// categoryImageUrl: zonder versie van de server blijft de oude handmatige
// cache-buster (?v=3) staan, zodat er niets terugvalt naar een foto die nog van
// vóór ?v=3 in een cache zit. Caddy ziet ?v=3 niet als versie (geen 12 hex-
// tekens) en geeft hem een uur, dus een vervangen foto komt ook dan door.
export function categoryImageUrl(name: string, version?: string | null): string {
  return imageUrl("category", categorySlug(name), version || "3");
}

// recipeImageUrl: de bestandsnaam staat in recipe.image.
export function recipeImageUrl(recipe: { image: string; image_version?: string }): string {
  return imageUrl("recipe", recipe.image, recipe.image_version);
}
